import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { departmentsApi } from '../../../api/admin/departments';
import { ApiError } from '../../../api/client';
import { IconActionButton } from '../../../components/IconActionButton';
import { Power, Eye } from 'lucide-react';
import { SectionHeader } from '../../../components/SectionHeader';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable } from '../../../components/DataTable';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';

// Mirrors the shape of the backend's departmentSchema (department.controller.ts)
// for instant client-side feedback only - the backend's Zod schema and
// AdminSafetyService checks remain the actual authority.
const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters.'),
  code: z.string().max(30).optional(),
});
type FormValues = z.infer<typeof formSchema>;

export function DepartmentsPage() {
  const canManage = usePermission('admin.departments.manage');
  const [modalOpen, setModalOpen] = useState(false);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: () => departmentsApi.list() });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => departmentsApi.create({ name: values.name, code: values.code || null }),
    onSuccess: () => {
      showSuccess('Department created.');
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setModalOpen(false);
      reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create department.'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => departmentsApi.deactivate(id),
    onSuccess: () => {
      showSuccess('Department deactivated.');
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate department.'),
  });

  return (
    <div className="page">
      <PageHeader title="Departments" actions={canManage && <button className="btn-primary" onClick={() => setModalOpen(true)}>New Department</button>} />

      {departmentsQuery.isLoading && <p>Loading…</p>}
      {departmentsQuery.isError && <p className="error-text">Could not load departments.</p>}

      {departmentsQuery.data && (
        <div className="panel">
          <SectionHeader title="All Departments" />
          <div className="panel-body">
        <DataTable
          rows={departmentsQuery.data.data}
          rowKey={(d) => d.id}
          emptyMessage="No departments yet."
          columns={[
            { header: 'Name', render: (d) => d.name },
            { header: 'Code', render: (d) => d.code ?? '-' },
            { header: 'Status', render: (d) => (d.is_active ? 'Active' : 'Inactive') },
            {
              header: '',
              render: (d) => (
                <span className="icon-action-group">
                  <IconActionButton icon={Eye} label="View" onClick={() => navigate(`/admin/departments/${d.id}`)} />
                  {canManage && d.is_active && <IconActionButton icon={Power} label="Deactivate" onClick={() => deactivateMutation.mutate(d.id)} />}
                </span>
              ),
            },
          ]}
        />
          </div>
        </div>
      )}

      {modalOpen && (
        <Modal title="New Department" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Name
              <input {...register('name')} />
              {errors.name && <span className="field-error">{errors.name.message}</span>}
            </label>
            <label>
              Code (optional)
              <input {...register('code')} />
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Saving…' : 'Create'}
              </button>
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
